// Pure permission predicates for the navigation model ([NAV-MASKED-PERMISSIONS]).
//
// These are the permission rules the nav model consumes (`isClubOfficer`,
// `canManageClub`, `canManageDistrict`). They are pure functions of a
// masked-auth snapshot so they are trivially unit-testable and, crucially,
// carry NO dependency on the current route - visibility is a function of
// permission only. Callers pass the auth store's masked user so the
// "View as Member" toggle is honoured automatically.

import type { ClubMembership, UserInfo } from "@/core/types";

/** OfficerRole is not exported from core/types; derive it from UserInfo. */
export type OfficerRole = NonNullable<UserInfo["officerRoles"]>[number];

/** The subset of the masked auth store the nav model reads. */
export interface NavAuthContext {
  isAuthenticated: boolean;
  isAdmin: boolean;
  user: Pick<UserInfo, "id" | "clubMemberships" | "officerRoles"> | null | undefined;
}

function matchesShortName(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

function membershipForClubShortName(auth: NavAuthContext, shortName: string): ClubMembership | undefined {
  return auth.user?.clubMemberships?.find((m) => matchesShortName(m.club.shortName, shortName));
}

/** Mirrors WorkspaceSidebar `isOfficer`. */
export function isClubOfficer(auth: NavAuthContext, shortName: string): boolean {
  if (auth.isAdmin) return true;
  return Boolean(membershipForClubShortName(auth, shortName)?.isOfficer);
}

/**
 * Mirrors WorkspaceSidebar `isManagerOrOfficer` (gates `requiresManagement` items). The EasySpeak
 * review page and its banner read it too, matching the server's `canManage` gate on those endpoints.
 */
export function canManageClub(auth: NavAuthContext, shortName: string): boolean {
  if (auth.isAdmin) return true;
  const m = membershipForClubShortName(auth, shortName);
  return Boolean(m?.isManager || m?.isOfficer);
}

/** Mirrors WorkspaceSidebar `extractDistrictNumber` - the leading digits of a unit label. */
export function extractDistrictNumber(value: string | undefined | null): string | null {
  if (!value) return null;
  const match = value.match(/(\d+)/);
  return match?.[1] ?? null;
}

/**
 * Mirrors WorkspaceSidebar `canManageDistrict`, MINUS the route gate.
 *
 * This is the crux of the bug fix: district officer visibility depends only on
 * an active District officer role (or admin), never on whether the browser is
 * currently on that district's route.
 */
export function canManageDistrict(auth: NavAuthContext, districtId: string): boolean {
  if (auth.isAdmin) return true;
  return Boolean(
    auth.user?.officerRoles?.some(
      (role) => role.unitType === "District" && role.isActive && extractDistrictNumber(role.unit) === districtId,
    ),
  );
}
